import React, {useRef, useState} from "react";
import PropTypes from "prop-types";
import DropDownBase from "./base/DropDownBase";
import FlexContainer from "../../layout/FlexContainer";
import Text from "../../display/Text";
import SvgIcon from "../../display/SvgIcon";
import {buildStyles} from "../../buildStyles";
import {useTheme} from "../../../theme/ThemeProvider";
import Container from "../../layout/Container";
import ICONS from "connectui/icons/iconMap";
import OutlinedInputBase from "../text/OutlinedInputBase";
import TextInput from "../text/type/TextInput";

const useStyles = (theme) =>
  buildStyles({
    dropdownContainer: {
      position: "relative",
      display: "inline-block",
      width: "100%",
    },
    dropdownItem: {
      cursor: "pointer",
      padding: theme.spacing(2.5, 3.5),
      backgroundColor: theme.colors.white,
      "&:hover": {backgroundColor: theme.colors.gray50},
      "&.selected": {backgroundColor: theme.colors.gray50},
      "&.disabled": {
        cursor: "default",
        opacity: 0.5,
        backgroundColor: "transparent !important",
      },
    },
    labelContainer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      gap: theme.spacing(1.5),
    },
    arrowIcon: {
      width: "8px",
      height: "8px",
      marginTop: 1,
      color: theme.colors.gray400,
    },
    checkIcon: {
      width: "20px",
      height: "20px",
      color: theme.colors.primary,
      flex: 1,
      marginLeft: theme.spacing(2),
      alignSelf: "end",
      justifyContent: "flex-end !important",
    },
    searchIcon: {
      width: "15px",
      height: "15px",
      color: theme.colors.textPrimary,
      marginRight: theme.spacing(2),
    },
    xIcon: {
      width: "11px",
      height: "11px",
      cursor: "pointer",
      color: theme.colors.gray500,
    },
    searchContainer: {
      position: "sticky",
      top: 0,
      backgroundColor: theme.colors.white,
      zIndex: 1,
      padding: theme.spacing(2, 2),
    },
    itemsContainer: {
      maxHeight: "200px",
      overflowY: "auto",
    },
  });

const DropDownSelect = ({
  label,
  selected,
  customLabel = null,
  children,
  dropDownBase: DropDownBaseComponent = DropDownBase,
  onChange = () => {},
  search,
  ...props
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const lastToggleTime = useRef(0);

  const handleToggle = () => {
    const now = Date.now();
    if (now - lastToggleTime.current < 300) {
      return;
    }

    setIsOpen((prevOpen) => !prevOpen);
    lastToggleTime.current = now;
  };

  const handleClose = () => {
    setIsOpen(false);
    lastToggleTime.current = Date.now();
  };

  const handleSelect = (value, label) => {
    setSelectedLabel(label);
    onChange(value);
    handleClose();
  };

  const defaultLabel = (
    <Text
      as="div"
      variant="textXS"
      weight="medium"
      color="gray700"
      sx={{overflow: "hidden"}}
    >
      {selectedLabel || label || "Select an option"}
    </Text>
  );

  const filteredChildren = React.Children.toArray(children).filter((child) => {
    if (!searchQuery) return true;
    const childLabel = child.props.label || "";
    return childLabel.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <Container ref={dropdownRef} className={classes.dropdownContainer}>
      <DropDownBaseComponent
        label={
          <Container className={classes.labelContainer}>
            {customLabel || defaultLabel}
            <SvgIcon
              component={ICONS[isOpen ? "chevron-up" : "chevron-down"]}
              className={classes.arrowIcon}
            />
          </Container>
        }
        isOpen={isOpen}
        onClick={handleToggle}
        onClose={handleClose}
        {...props}
      >
        {search && (
          <Container className={classes.searchContainer}>
            <TextInput
              inputBase={OutlinedInputBase}
              startIcon={
                <SvgIcon
                  component={ICONS.search}
                  className={classes.searchIcon}
                  sx={{color: theme.colors.gray500}}
                />
              }
              inputProps={{
                placeholder: "Search",
                value: searchQuery,
                onChange: (e) => setSearchQuery(e.target.value),
              }}
              sx={{
                padding: theme.spacing(1, 3.5),
              }}
              actions={
                !!searchQuery && (
                  <SvgIcon
                    component={ICONS.x}
                    className={classes.xIcon}
                    onClick={() => setSearchQuery("")}
                  />
                )
              }
            />
          </Container>
        )}
        <Container className={classes.itemsContainer}>
          {filteredChildren.map((child, index) => {
            const {value, disabled, label: childLabel} = child.props;
            const isSelected = selected === value;
            return (
              <FlexContainer
                key={index}
                className={`${classes.dropdownItem} ${isSelected ? "selected" : ""} ${disabled ? "disabled" : ""}`}
                onClick={() =>
                  disabled ? {} : handleSelect(value, childLabel)
                }
              >
                {child}
                {isSelected && (
                  <SvgIcon
                    component={ICONS.check}
                    className={classes.checkIcon}
                  />
                )}
              </FlexContainer>
            );
          })}
        </Container>
      </DropDownBaseComponent>
    </Container>
  );
};

DropDownSelect.propTypes = {
  label: PropTypes.node,
  selected: PropTypes.any,
  children: PropTypes.node.isRequired,
  dropDownBase: PropTypes.any,
  onChange: PropTypes.func,
  search: PropTypes.bool,
};

export default DropDownSelect;
